// @flow

import * as React from 'react';
import { css } from '@emotion/react';
import { Flex, Box } from 'react-system';
import { useTheme } from '../../theme';
import * as icons from '../../icons';

export type Styles = {|
  chip?: Object,
|};

type ActiveColor = $ReadOnly<{|
  main: string,
  active: string,
|}>;

type Props = {|
  label: React.Node,
  empty: boolean,
  bg: string | ActiveColor,
  color: ActiveColor,
  active: boolean,
  open: boolean,
  onClick?: (e: SyntheticEvent<EventTarget>) => void,
  onDelete: (e: SyntheticEvent<EventTarget>) => void,
  timeout?: number,
  Icon?: React.AbstractComponent<{| size: number |}>,
  borderRadius: number,
  hasCloseIcon: boolean,
  checkedFiltersVariant?: boolean,
  styles?: Styles,
|};

const Chip = props => {
  const highlight = hover => ({
    // css hack to highlight color
    boxShadow: props.active
      ? `inset 10000px 0 0 rgba(255,255,255, ${hover ? 0.2 : 0.0})`
      : `inset 10000px 0 0 rgba(255,255,255, ${hover ? 0.1 : 0.0})`,
    transition: `all ${props.timeout / 1000}s ease`,
  });
  const children: React.Node = (props.children: any);
  const height = props.checkedFiltersVariant ? 38 : 36;
  const borderOpacity = props.checkedFiltersVariant ? 0.23 : 0.1;
  const chipStyle = css`
    user-select: none;
    cursor: pointer;
    border: 1px solid rgba(0, 0, 0, ${props.hasItems ? 0 : borderOpacity});
    border-radius: ${props.borderRadius}px;
    height: ${height}px;
    justify-content: space-between;
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
    ${props.bg && `background-color:${props.bg};`}
    ${highlight(false)};
    &:hover {
      ${highlight(true)};
    }
  `;
  return (
    <Flex
      css={props.styles?.chip ?? chipStyle}
      onClick={props.onClick}
      alignItems="center"
    >
      {children}
    </Flex>
  );
};

export const FilterItem = (props: Props): React.Node => {
  const { empty = true, timeout = 250 } = props;

  const { text } = useTheme();

  return (
    <Chip
      onClick={props.onClick}
      active={props.active}
      hasItems={!empty}
      timeout={timeout}
      bg={
        typeof props.bg === 'string'
          ? props.bg
          : !empty
          ? props.bg.active
          : props.bg.main
      }
      color={props.active ? props.color.active : props.color.main}
      borderColor={props.active ? props.color.active : props.color.main}
      borderRadius={props.borderRadius}
      checkedFiltersVariant={props.checkedFiltersVariant === true}
      styles={props.styles}
    >
      {props.Icon && (
        <Box pl={['8px', '12px']}>
          <props.Icon size={20} />
        </Box>
      )}
      <Box
        ml={props.Icon ? 2 : 3}
        mr={1}
        css={[
          text.ellipsis,
          props.checkedFiltersVariant === true ? text.subtitle2 : {},
        ]}
      >
        {props.label}
      </Box>
      <Box
        mr={2}
        onClick={props.hasCloseIcon === false ? undefined : props.onDelete}
      >
        <Flex
          width="24px"
          height="24px"
          alignItems="center"
          justifyContent="center"
        >
          {(props.hasCloseIcon === false ? true : empty) ? (
            <icons.ExpandMore
              css={{
                transform: props.open ? 'rotate(180deg)' : 'none',
              }}
              size={24}
            />
          ) : (
            <icons.Close size={20} />
          )}
        </Flex>
      </Box>
    </Chip>
  );
};
